import React, { useEffect, useMemo, useRef } from 'react'
import { Box, alpha, useTheme } from '@mui/material'
import DOMPurify from 'dompurify'

interface AlertHTMLContentProps {
  html: string | undefined | null
}

const AlertHTMLContent: React.FC<AlertHTMLContentProps> = ({ html }) => {
  const theme = useTheme()
  const contentRef = useRef<HTMLDivElement>(null)

  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html ?? '', {
        FORBID_TAGS: ['style', 'script', 'meta', 'link', 'title', 'head'],
        ADD_ATTR: ['target']
      }),
    [html]
  )

  useEffect(() => {
    if (!contentRef.current) return
    contentRef.current.querySelectorAll('a').forEach((a) => {
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    })
  }, [sanitized])

  return (
    <Box
      ref={contentRef}
      dangerouslySetInnerHTML={{ __html: sanitized }}
      sx={{
        width: '100%',
        color: 'text.primary',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 15,
        lineHeight: 1.6,
        wordBreak: 'break-word',
        '& *': {
          backgroundColor: 'transparent !important',
          borderColor: `${alpha(theme.palette.text.primary, 0.12)} !important`,
          maxWidth: '100% !important',
          boxSizing: 'border-box'
        },
        '& *:not(a)': {
          color: `${theme.palette.text.primary} !important`
        },
        '& table': {
          width: '100% !important',
          borderCollapse: 'collapse',
          my: 1.5
        },
        '& th, & td': {
          padding: '8px 12px',
          border: `1px solid ${alpha(theme.palette.text.primary, 0.12)} !important`,
          textAlign: 'left',
          verticalAlign: 'top'
        },
        '& th': {
          fontWeight: 600,
          backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`
        },
        '& a': {
          color: `${theme.palette.primary.main} !important`,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' }
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          fontWeight: 600,
          mt: 2,
          mb: 1,
          lineHeight: 1.3
        },
        '& h1': { fontSize: '1.5rem' },
        '& h2': { fontSize: '1.3rem' },
        '& h3': { fontSize: '1.15rem' },
        '& p': { my: 1 },
        '& ul, & ol': { pl: 3, my: 1 },
        '& li': { my: 0.5 },
        '& blockquote': {
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          pl: 2,
          ml: 0,
          my: 1.5,
          fontStyle: 'italic',
          opacity: 0.9
        },
        '& hr': {
          border: 'none',
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          my: 2
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.9em',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
          backgroundColor: `${alpha(theme.palette.text.primary, 0.08)} !important`
        },
        '& pre': {
          p: 1.5,
          borderRadius: 1,
          overflow: 'auto',
          backgroundColor: `${alpha(theme.palette.text.primary, 0.06)} !important`
        },
        '& font': {
          fontFamily: 'inherit !important',
          fontSize: 'inherit !important'
        }
      }}
    />
  )
}

export default AlertHTMLContent
